import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchGLDashboardDataService } from '../../services/glDashboard/GLDashboardService';
import { OptimizationSummaryItem, WellSummaryKey } from '../../types/GLDashboardTypes';

export interface GLDashboardState {
  loading: boolean;
  error?: string | null;
  count: Record<WellSummaryKey, number> | null;
  paretoCounts: Record<string, number>;
  chartData: any[];
  HighDaysSinceTested: number;
  optimizationData: OptimizationSummaryItem[];
}

const initialState: GLDashboardState = {
  loading: false,
  error: null,
  count: null,
  paretoCounts: {},
  chartData: [],
  HighDaysSinceTested: 0,
  optimizationData: [],
};

export const fetchGLDashboard = createAsyncThunk(
  'glDashboard/fetch',
  async (
    params: {
      areaIds?: string[];
      businessUnitIds?: string[];
      fieldIds?: string[];
      wellIds?: string[] | string;
    } = {},
    thunkAPI,
  ) => {
    try {
      const data = await fetchGLDashboardDataService(params);
      return data;
    } catch (err: any) {
      const message = err?.response?.data?.message || err.message || 'Failed to fetch ESP dashboard data';
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const glDashboardSlice = createSlice({
  name: 'glDashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGLDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGLDashboard.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = null;
        state.count = {
          WellsEnabled: action.payload.WellsEnabled,
          WellsRunning: action.payload.WellsRunning,
          WellsNoTests: action.payload.WellsNoTests,
          CountHighDaysSinceTested: action.payload.CountHighDaysSinceTested,
          WellsOutOfService: action.payload.WellsOutOfService,
        };
        state.HighDaysSinceTested = action.payload.HighDaysSinceTested;
        state.paretoCounts = {
          ParetoPercOver90: action.payload.ParetoPercOver90,
          ParetoPercWells80: action.payload.ParetoPercWells80,
          ParetoPercWells80To90: action.payload.ParetoPercWells80To90,
          ParetoCountWells80: action.payload.ParetoCountWells80,
          ParetoCountWells80To90: action.payload.ParetoCountWells80To90,
          ParetoCountWellsOver90: action.payload.ParetoCountWellsOver90,
        };
        state.chartData = action.payload.FieldPotentialData || [];
        state.optimizationData = [
          {
            text: 'Injection rate change',
            wellcount: action.payload.InjectionRateChangeWellsCount,
            value: action.payload.InjectionRateChangeOilAdditional,
            navigateState: { tab: 'gl', widget: 'glRecommendationsInjectionRate' },
          },
          {
            text: 'Injection depth change',
            wellcount: action.payload.InjectionDepthChangeWellsCount,
            value: action.payload.InjectionDepthChangeOilAdditional,
            navigateState: { tab: 'gl', widget: 'glRecommendationsDepthChange' },
          },
        ];
      })
      .addCase(fetchGLDashboard.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch GL dashboard data';
      });
  },
});

export default glDashboardSlice.reducer;

// Selectors
export const selectGLDashboardLoading = (state: any) => state.glDashboard?.loading ?? false;
export const selectGLDashboardError = (state: any) => state.glDashboard?.error ?? null;
export const selectGLSummaryCount = (state: any) => state.glDashboard?.count;
export const selectGLParetoCounts = (state: any) => state.glDashboard?.paretoCounts;
export const selectGLChartData = (state: any) => state.glDashboard?.chartData;
export const selectGLHighDaysSinceTested = (state: any) => state.glDashboard?.HighDaysSinceTested || 0;
export const selectGLOptimizationData = (state: any) => state.glDashboard?.optimizationData || [];
